import Phaser from 'phaser';

import { GameSizeMetrics } from 'src/games/common/phaser/game/scenes/scaled-base-scene';
import { BlocksMainScene } from '../../scenes/blocks-main-scene';
import { LevelViewModel } from './level-view-model';
import { Subscription } from 'rxjs';
import { TextResizer } from '../grid/text-resizer';

export class LevelView extends Phaser.GameObjects.Container {
  private subscriptionGroup: Subscription;
  private textResizer: TextResizer;

  private viewModel: LevelViewModel;
  private enableAnimations: boolean;
  private tweenDistance: number;
  private currentScaleFactor = 1;
  private scaledOffsetX = 0;

  constructor(scene: BlocksMainScene, x: number, y: number, viewModel: LevelViewModel) {
    super(scene, x, y);

    this.subscriptionGroup = new Subscription();
    this.textResizer = new TextResizer(scene, this, {
      fontFamily: viewModel.defaultFontFamily,
      fontSize: '150px',
      fontStyle: 'bold',
      color: '#ffffff',
    });

    this.viewModel = viewModel;
    this.enableAnimations = false;
    this.tweenDistance = 100;

    this.setName(LevelView.name);
    scene.add.existing(this);

    this.subscriptionGroup.add(
      this.viewModel.onGameSetupCompleteTrigger.subscribe(() => {
        this.enableAnimations = true;
      }),
    );
    this.subscriptionGroup.add(
      this.viewModel.onResetGameScene$.subscribe(() => {
        this.enableAnimations = false;
      }),
    );
    this.subscriptionGroup.add(
      this.viewModel.blocksLogicActionBus$.subscribe((actionBus) => {
        if (!actionBus) {
          return;
        }
        actionBus.on('onNextStage', (payload) => {
          if (this.enableAnimations) {
            this.animateLevel(payload.level);
          }
        });
      }),
    );
  }

  override destroy(fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(fromScene);
  }

  public onResize(gameSizeMetrics: GameSizeMetrics) {
    const layoutConfig = this.viewModel.layoutConfig;
    const calc = this.viewModel.calculateScaleFactor(gameSizeMetrics);
    this.currentScaleFactor = calc.scaleFactor;
    this.scaledOffsetX = calc.scaledOffsetX;

    const xPos = (layoutConfig.boardWidth + layoutConfig.hMargin * 2) * calc.scaleFactor * 0.5;
    const yPos = (layoutConfig.vMargin + layoutConfig.boardHeight * 0.5) * calc.scaleFactor;

    this.tweenDistance = layoutConfig.boardWidth * 0.2 * calc.scaleFactor;

    this.setPosition(xPos + calc.scaledOffsetX, yPos);
  }

  private animateLevel(level: number) {
    if (level < 2) {
      return;
    }

    const layoutConfig = this.viewModel.layoutConfig;
    const cellSz = layoutConfig.boardWidth / 9;
    const scaledCellSz = cellSz * this.currentScaleFactor;

    const inDuration = 400;
    const outDuration = 600;

    const fontSize = this.textResizer.fitText('XXXXXXXXXX', scaledCellSz * 6, 14);

    const levelTitleLabel = this.scene.add
      .text(
        this.x - this.tweenDistance,
        this.y - scaledCellSz * 0.25,
        this.viewModel.levelLabelText,
        {
          fontFamily: this.viewModel.defaultFontFamily,
          fontStyle: 'bold',
          color: this.viewModel.primaryLabelFill,
        },
      )
      .setFontSize(fontSize)
      .setOrigin(0.5, 1.0)
      .setAlpha(0.0)
      .setDepth(600);

    this.scene.tweens.add({
      targets: [levelTitleLabel],
      x: this.x,
      alpha: 1.0,
      duration: inDuration,
      ease: 'Quad.Out',
      onComplete: () => {
        this.scene.tweens.add({
          targets: [levelTitleLabel],
          x: this.x + this.tweenDistance * 2,
          alpha: 0,
          duration: outDuration,
          ease: 'Quart.In',
          onComplete: () => {
            levelTitleLabel.destroy();
          },
        });
      },
    });

    const levelLabel = this.scene.add
      .text(this.x + this.tweenDistance, this.y - scaledCellSz * 0.25, `${level}`, {
        fontFamily: this.viewModel.defaultFontFamily,
        fontStyle: 'bold',
        color: this.viewModel.primaryLabelFill,
      })
      .setFontSize(fontSize * 1.5)
      .setOrigin(0.5, 0.0)
      .setAlpha(0.0)
      .setDepth(600);

    this.scene.tweens.add({
      targets: [levelLabel],
      x: this.x,
      alpha: 1.0,
      duration: inDuration,
      ease: 'Quad.Out',
      onComplete: () => {
        this.scene.tweens.add({
          targets: [levelLabel],
          x: this.x - this.tweenDistance * 2,
          alpha: 0,
          duration: outDuration,
          ease: 'Quart.In',
          onComplete: () => {
            levelLabel.destroy();
          },
        });
      },
    });

    const levelTitleBackground = this.scene.add
      .rectangle(
        this.x,
        this.y,
        scaledCellSz * 9,
        scaledCellSz * 3,
        this.viewModel.levelBackgroundFill,
      )
      .setOrigin(0.5, 0.5)
      .setAlpha(0.0)
      .setDepth(599);

    this.scene.tweens.add({
      targets: [levelTitleBackground],
      alpha: 0.95,
      duration: inDuration,
      ease: 'Quad.Out',
      onComplete: () => {
        this.scene.tweens.add({
          targets: [levelTitleBackground],
          alpha: 0,
          duration: outDuration,
          ease: 'Quart.In',
          onComplete: () => {
            levelTitleBackground.destroy();
          },
        });
      },
    });
  }
}
